import { useState, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Mic, MicOff } from 'lucide-react'
import { assegnazioniCantiereApi } from '../services/api'

interface TerminaAttivitaModalProps {
  assegnazioneId: number
  commessaNome: string
  onClose: () => void
  onSuccess: () => void
}

export default function TerminaAttivitaModal({ assegnazioneId, commessaNome, onClose, onSuccess }: TerminaAttivitaModalProps) {
  const [descrizione, setDescrizione] = useState('')
  const [ascolto, setAscolto] = useState(false)
  const recognitionRef = useRef<any>(null)

  const SpeechRecognitionCtor: any =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition

  const toggleDettatura = () => {
    if (!SpeechRecognitionCtor) return

    if (ascolto) {
      recognitionRef.current?.stop()
      setAscolto(false)
      return
    }

    const recognition = new SpeechRecognitionCtor()
    recognition.lang = 'it-IT'
    recognition.continuous = true
    recognition.interimResults = false

    recognition.onresult = (event: any) => {
      let testo = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        testo += event.results[i][0].transcript
      }
      setDescrizione(prev => (prev ? `${prev} ${testo}` : testo))
    }
    recognition.onerror = (event: any) => {
      setAscolto(false)
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        toast.error('Microfono non autorizzato. Su connessione non sicura (HTTP) il browser blocca l\'accesso al microfono.')
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error(`Errore dettatura: ${event.error}`)
      }
    }
    recognition.onend = () => setAscolto(false)

    recognitionRef.current = recognition
    recognition.start()
    setAscolto(true)
  }

  const mutation = useMutation({
    mutationFn: () => assegnazioniCantiereApi.terminaAttivita(assegnazioneId, descrizione.trim()),
    onSuccess: (res) => {
      if (res.data.email_inviata) {
        toast.success('Rapportino inviato')
      } else {
        toast.error(`Rapportino salvato, invio email non riuscito (${res.data.email_errore || 'errore'})`)
      }
      onSuccess()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Errore durante il salvataggio del rapportino')
    },
  })

  const descrizioneValida = descrizione.trim().length > 0

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[300] p-4">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-1">Termina attività</h2>
        <p className="text-sm text-gray-500 mb-4">{commessaNome}</p>

        <label className="label">Descrizione attività svolte</label>
        <div className="flex gap-2 items-start mb-1">
          <textarea
            className="input flex-1"
            rows={5}
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            placeholder="Descrivi le attività svolte sulla commessa..."
          />
          <button
            type="button"
            className="btn-secondary px-3"
            disabled={!SpeechRecognitionCtor}
            title={SpeechRecognitionCtor ? 'Dettatura vocale' : 'Dettatura non supportata su questo browser'}
            onClick={toggleDettatura}
          >
            {ascolto ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-secondary" onClick={onClose}>Annulla</button>
          <button
            className="btn-primary"
            disabled={!descrizioneValida || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Invio...' : 'Invia rapportino'}
          </button>
        </div>
      </div>
    </div>
  )
}
